import { useEffect, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { useTran } from "@/hooks";
import { FormField, SelectField } from "@/components/common";
import { Button } from "@/components/ui/button";
import { ROLES } from "@/lib/constants/options";
import { useAuthStore } from "@/features/auth";
import { useActivityLogStore } from "../hooks";

const ActivityLogFilters = observer(() => {
  const { t } = useTran();
  const { user } = useAuthStore();
  const {
    filters,
    users,
    usersLoading,
    usersLoaded,
    loadUsers,
    setFilters,
    resetFilters,
  } = useActivityLogStore();

  const isAdminRole = [
    ROLES.SYSTEM_ADMIN,
    ROLES.BUSINESS_UNIT,
    ROLES.SUPER_ADMIN,
  ].includes(user?.role);

  useEffect(() => {
    if (!isAdminRole || usersLoading || usersLoaded) return;
    loadUsers();
  }, [isAdminRole, loadUsers, usersLoaded, usersLoading]);

  const moduleOptions = useMemo(() => {
    const modules = user?.moduleNames || [];

    const dynamicOptions = modules
      .filter((m) => m?.key && m?.title)
      .map(({ key, title }) => ({
        key,
        label: t(title),
      }));

    return [
      { key: "All", label: t("All") },
      { key: "Auth", label: t("Auth") },
      ...dynamicOptions,
    ];
  }, [user?.moduleNames, t]);

  const userOptions = useMemo(() => {
    const options = users.map((item) => ({
      key: item?._id || item?.id || "",
      label: item?.name || item?.username || item?.email || "",
    }));

    return [{ key: "All", label: t("All") }, ...options].filter(
      (item) => item.key,
    );
  }, [t, users]);

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-6">
      {isAdminRole ? (
        <SelectField
          label={t("Users")}
          value={filters.selectedUserId}
          onValueChange={(value) => setFilters({ selectedUserId: value })}
          options={userOptions}
          placeholder={t("Select user")}
          disabled={usersLoading}
        />
      ) : null}

      <SelectField
        label={t("Module")}
        value={filters.module}
        onValueChange={(value) => setFilters({ module: value })}
        options={moduleOptions}
        placeholder={t("Select module")}
      />

      <FormField
        label={t("Search")}
        placeholder={t("Search")}
        value={filters.searchText}
        onChange={(event) => setFilters({ searchText: event.target.value })}
      />

      <FormField
        label={t("Start Date")}
        type="date"
        value={filters.startDate}
        onChange={(event) => setFilters({ startDate: event.target.value })}
      />

      <FormField
        label={t("End Date")}
        type="date"
        value={filters.endDate}
        onChange={(event) => setFilters({ endDate: event.target.value })}
      />

      <div className="flex items-end">
        <Button
          type="button"
          variant="clear"
          onClick={resetFilters}
          className="h-10 w-full justify-start px-0 text-sm sm:w-auto sm:justify-end"
        >
          {t("Clear All Filters")}
        </Button>
      </div>
    </div>
  );
});

export default ActivityLogFilters;
